<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal
          size="lg" :visible="modal.show" :title="$t('Transfer Item(s) to Another Branch')" :ok-title="$t('Submit')"
          scrollable @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)">
      <spinner show v-if="modal.loading" height="20"/>
      <b-form v-else @submit.prevent="handleSubmit(onSubmit)">
        <div class="row">
          <div class="col-md-3">
            <input-widget :model="model" attribute="branch_uuid" type="select2" :items="modal.branches"
                          value-field="uuid" display-field="${item.name}"/>
          </div>
          <div class="col-md-3">
            <receipt-select-input-widget :model="model" attribute="receipt_uuid" :branch-uuid="model.branch_uuid"
                          :disabled="!model.branch_uuid"/>
          </div>
          <div class="col-md-3">
            <input-widget :model="model" attribute="item_location_uuid" type="select2" :items="itemLocations"
                          value-field="uuid" display-field="${item.name}"/>
          </div>
          <div class="col-md-3">
            <input-widget :model="model" attribute="note" type="textarea"/>
          </div>
        </div>
      </b-form>
      <div class="row">
        <div class="col">{{ $t('Selected Items') + ': ' + modal.items.length }}</div>
      </div>
      <artisio-table :items="modal.items" :fields="tableFields">
        <template slot="cell(stock_no)" slot-scope="{item: inventory}">
          {{ inventory.stock_no }}
        </template>
        <template slot="cell(description)" slot-scope="{item: inventory}">
          {{ inventory.dynamic_fields | attributeByMasterLanguage('description') | striptags | truncate(50) }}
        </template>
        <template slot="cell(department)" slot-scope="{item: inventory}">
          {{ inventory.department.name | masterLanguage }}
        </template>
        <template slot="cell(category)" slot-scope="{item: inventory}">
          {{ inventory.category.name | masterLanguage }}
        </template>
      </artisio-table>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal";
  import Spinner from "@/core/components/common/Spinner";
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import TransferAcrossBranchesModel from "./TransferAcrossBranches.model";
  import InventoryService from "@/modules/inventory/InventoryService";
  import ReceiptSelectInputWidget from "@/core/widgets/ReceiptSelectInputWidget";
  import {createNamespacedHelpers} from "vuex";

  const {mapState, mapActions} = createNamespacedHelpers('inventory');

  export default {
    name: "TransferAcrossBranchesModal",
    components: {
      InputWidget,
      ArtisioModal,
      ArtisioTable,
      Spinner,
      ReceiptSelectInputWidget
    },
    data() {
      return {
        model: new TransferAcrossBranchesModel(),
        itemLocations: [],
        tableFields: [
          {key: 'stock_no', label: this.$t('Receipt no / stock no'), sortable: false},
          {key: 'description', label: this.$t('Description'), tdClass: 'description-col', sortable: false},
          {key: 'department', label: this.$t('Department'), sortable: false},
          {key: 'category', label: this.$t('Category'), sortable: false},
        ]
      }
    },
    computed: {
      ...mapState({
        modal: state => state.transferAcrossBranchesModal,
      }),
    },
    watch: {
      'model.branch_uuid': {
        handler(newVal) {
          this.$nextTick(() => {
            this.$set(this.model, 'receipt_uuid', null);
            this.$set(this.model, 'item_location_uuid', null);

            if (newVal) {
              this.itemLocations = this.modal.branchUuidItemLocationsMap[newVal];
            } else {
              this.itemLocations = [];
            }
          });
        }
      }
    },
    methods: {
      ...mapActions(['hideTransferAcrossBranchesModal']),
      onHidden() {
        this.model = new TransferAcrossBranchesModel();
        this.hideTransferAcrossBranchesModal();
      },
      async onSubmit() {
        if (this.modal.loading) {
          return;
        }
        let success = await this.$confirm(
          this.$t(`<b>Before transferring items to a new branch, please read the key details:</b>
          <ul>
          <li>If the Receipt field is left blank in the transfer window, items will be transferred into a newly generated receipt under the destination branch with a different number not corresponding to the original receipt.</li>
          <li>The transferred items will still be stored in the archive of the original branch. To view them, go to Stock > Inventory, enable the Transferred checkbox, and use the Transferred Branch dropdown filter.</li>
          <li>Once archived, transferred items can only be reverted by transferring them back from the destination branch.</li>
          <li>Once transferred back to the previous branch, any item updates made during the transfer will be retained.</li>
          <li>Notes will be created at the item and receipt level to capture the transfer record in both the original and destination branches.</li>
          <li>If only some items from a receipt are moved, the receipt in the original branch will still include the non-transferred items.</li>
          <li>The commission and L&D will remain the same as in the original receipt in the original branch. Please adjust them in the destination branch if needed.</li>
          <li>Vendor terms on the item level will be transferred unchanged.</li>
          <li>If a new receipt is created in the destination branch (i.e., the Receipt No in the transfer modal is left blank), existing vendor charges at the original branch level that are active on the receipt level will be ignored. A new receipt in the destination branch will inherit the vendor charges active in that branch. However, if any vendor charges were overridden on the original receipt level, the newly created receipt in the destination branch will also include those vendor charges. Items will inherit vendor charges accordingly if applicable.</li>
          <li>If a branch operates in another currency, the financial information of the items—such as estimates, start price, reserve, and buy-now price—will not be adjusted based on exchange rates. Please adjust them manually after the transfer.</li>
          </ul>`),
          null,
          null,
          null,
          this.$t('Item Transfer Terms to Another Branch'),
          this.$t('Yes'),
          this.$t('No'),
          null,
          {
            label: this.$t("Please type 'confirm' to proceed"),
            text: 'confirm'
          }
        )
        if (!success) {
          return;
        }
        this.model.uuids = this.modal.items.map(item => item.uuid);
        this.modal.loading = true;
        let res = await InventoryService.transferAcrossBranches(this.model);
        this.modal.loading = false;
        if (res.success) {
          this.hideTransferAcrossBranchesModal();
          this.$toast('success', this.$t('Transfer started'));
        } else {
          if (res.body.non_field_errors) {
              this.$errorDialog(res.body.non_field_errors[0]);
            } else {
              this.$errorDialog(this.$t('failed to transfer'));
            }
        }
      }
    },
  }
</script>

<style lang="scss" scoped>
  .artisio-table {
    /deep/ {
      .description-col {
        max-width: 500px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
</style>
